import React, { FC, useEffect, useState } from "react";
import styled from "styled-components";
import {
  filterPagesByRole,
  ProfileLeftSideBarDataProps,
  leftSideBarData as sideBarData,
} from "./data";
import Colors from "../../../../styles/Colors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userDataStore } from "../../../../store/userData";
import { useSnapshot } from "valtio";
import { isBusinessDataStore } from "../../../../store/isBusinessData";
import { businessUserDataStore } from "../../../../store/businessUserData";
import { ProfileSections } from "./types";
import { mediaObj } from "../../../../styles/Media";
import {
  IoChevronDown,
  IoChevronForward,
  IoChevronUp,
  IoClose,
} from "react-icons/io5";

interface Props {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const LeftSideBar: FC<Props> = ({ open, onClose, onOpen }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const getType = searchParams.get("active");
  const { isBusinessData } = useSnapshot(isBusinessDataStore);
  const { businessUserData } = useSnapshot(businessUserDataStore);
  const { userData } = useSnapshot(userDataStore);
  const { role } = useSnapshot(isBusinessDataStore);
  const [leftSideBarData, setLeftSideBarData] = useState<
    ProfileLeftSideBarDataProps[]
  >([]);

  useEffect(() => {
    if (!isBusinessData) {
      setLeftSideBarData(sideBarData(isBusinessData));
      return;
    }
    setLeftSideBarData(
      filterPagesByRole(
        sideBarData(
          isBusinessData
          // !!businessUserData?.paymentInfo?.isSubscriptionMode
        ),
        role
      )
    );
  }, [businessUserData?.paymentInfo?.isSubscriptionMode, isBusinessData, role]);

  return (
    <Container open={open}>
      <Toggle isOpen={open} onClick={open ? onClose : onOpen}>
        {open ? <IoClose /> : <IoChevronForward />}
      </Toggle>
      <Top>
        Hello,{" "}
        {isBusinessData
          ? businessUserData?.businessInfo.name
          : userData?.userInfo?.name.split(" ")[0]}
        .
      </Top>
      <Bottom>
        {leftSideBarData.map((ev, i) => {
          const urlSplit = ev.url?.split("=")[1];

          // for the business side if document is active, make case management active
          const isActive =
            isBusinessData &&
            getType === ProfileSections.documents &&
            urlSplit === ProfileSections.caseManagement
              ? true
              : getType === urlSplit;

          //if isNew is true
          const isNew = ev.isNew;

          const isSubOpen =
            !!ev.subLink &&
            ev.subLink.some((item) => {
              const subUrlSplit = item.url?.split("=")[1];
              return getType === subUrlSplit;
            });

          return (
            <React.Fragment key={i}>
              <Nav
                key={i}
                onClick={() => {
                  if (ev.subLink) {
                    if (!isSubOpen) {
                      navigate(ev.subLink[0].url);
                    }
                  } else {
                    ev.url && navigate(ev.url);
                    open && onClose();
                  }

                  open && onClose();
                }}
                isActive={isActive}
              >
                <Circle isActive={isActive}>{ev.icon}</Circle>
                <Label isActive={isActive}>{ev.label}</Label>
                {isNew && <NewBadge>Improvised</NewBadge>}
                {ev.subLink && (
                  <>
                    {isSubOpen ? (
                      <IoChevronUp className="ml-auto text-[#0076E9]" />
                    ) : (
                      <IoChevronDown className="ml-auto text-[#0076E9]" />
                    )}
                  </>
                )}
              </Nav>
              {/* sublinks */}
              {isSubOpen && (
                <div className="ml-4 -mt-4">
                  {ev.subLink?.map((item, index) => {
                    const subUrlSplit = item.url?.split("=")[1];

                    const isSubActive =
                      isBusinessData &&
                      getType === ProfileSections.documents &&
                      urlSplit === ProfileSections.caseManagement
                        ? true
                        : getType === subUrlSplit;

                    return (
                      <Nav
                        key={index}
                        onClick={() => {
                          item.url && navigate(item.url);
                          open && onClose();
                        }}
                        isActive={isSubActive}
                      >
                        <Circle isActive={isSubActive}>{item.icon}</Circle>
                        <Label isActive={isSubActive}>{item.label}</Label>
                      </Nav>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </Bottom>
    </Container>
  );
};

export default LeftSideBar;

interface ContainerProps {
  open: boolean;
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  background-color: #f9fcff;

  ${mediaObj.smallDesktop} {
    width: 270px;
    height: 100%;
    position: absolute;
    left: 0;
    transform: ${({ open }) => (open ? "translateX(0)" : "translateX(-100%)")};
    transition: all 0.3s ease-in-out;
    z-index: 10;
  }
`;

const Top = styled.div`
  padding: 29px 11px;
  background-color: #0076e9;
  margin-bottom: 24px;
  border-radius: 12px 12px 0 0;
  color: ${Colors.White};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  text-transform: capitalize;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-bottom: 2rem;
`;

interface NavProps {
  isActive: boolean;
}

const Nav = styled.div<NavProps>`
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? "#0076E914" : "transparent"};
  border-left: ${({ isActive }) =>
    isActive ? `4px solid ${Colors.Blue00} ` : ""};
  padding: 11px 17px;
`;

interface CircleProps {
  isActive: boolean;
}

const Circle = styled.div<CircleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  shrink: 0;
  border-radius: 20%;
  width: 20px;
  height: 20px;
  color: ${({ isActive }) => (isActive ? Colors.Blue00 : Colors.GreyA7)};

  &:hover {
    transition: 0.2s ease-out;
  }
`;

interface LabelProps {
  isActive: boolean;
}

const Label = styled.div<LabelProps>`
  color: ${({ isActive }) => (isActive ? Colors.Blue00 : Colors.GreyA7)};
`;

const Toggle = styled.button<{ isOpen: boolean }>`
  width: 64px;
  border-radius: 0 8px 8px 0;

  padding: 0.6rem;

  background-color: ${Colors.Blue00};

  display: grid;
  place-content: center;

  position: fixed !important;
  left: 100%;
  top: 24px;

  opacity: ${({ isOpen }) => (isOpen ? "1" : "0.5")};

  transition: 300ms all ease;

  &:hover {
    opacity: 1;
  }

  & > svg {
    height: 32px;
    width: 32px;

    transition: 300ms all ease;

    color: white;
  }
`;

// New Badge Component
const NewBadge = styled.div`
  background: linear-gradient(135deg, #0075e9d5 0%, #0076e9 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  /* margin-left: auto; */
  white-space: nowrap;
  box-shadow: 0 2px 4px #0075e971;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4);
    }
  }
`;
